import React, { forwardRef, type ReactNode } from "react";
import { TextButton, Collapse, type CollapseRef } from "@SeemplicityDev/shared";

export interface SectionField {
  name?: string;
  size?: string;
  // The full field shape is built dynamically in the (untyped) useSections hook
  // and consumed by `getField`; only `name`/`size` are read here.
  // TODO: tighten once useSections field-building is typed.
  [key: string]: unknown;
}

export interface SectionRow {
  fields?: SectionField[];
}

export interface SectionData {
  name?: string;
  label?: ReactNode;
  description?: ReactNode;
  isCollapsable?: boolean;
  rows?: SectionRow[];
}

export interface SectionProps {
  section: SectionData;
  index: number;
  getField?: (field: SectionField) => ReactNode;
  initiallyOpen?: boolean;
  showConfirmButton?: boolean;
  onConfirmButtonClicked?: () => void;
  confirmButtonDisabled?: boolean;
}

const Section = forwardRef<CollapseRef, SectionProps>(
  (
    {
      section,
      index: sectionIndex,
      getField = () => null,
      initiallyOpen,
      showConfirmButton,
      onConfirmButtonClicked = () => {},
      confirmButtonDisabled,
    },
    ref
  ) => {
    const getRow = (row: SectionRow, key: string) => {
      return row?.fields?.map((field) => {
        return (
          <div
            className={`section-field section-field-${field?.size?.toLowerCase()}`}
            key={`${key}-field-${field?.name}`}
          >
            {getField(field)}
          </div>
        );
      });
    };

    const content = section?.rows?.map((row, index) => {
      return (
        <div
          className={"section-row-wrap"}
          key={`section-${sectionIndex}-row-${index}`}
        >
          <div className={"section-row"}>
            {getRow(row, `section-${sectionIndex}-row-${index}`)}
          </div>
        </div>
      );
    });

    return (
      <div className={"section"}>
        {section?.isCollapsable ? (
          <Collapse
            label={section?.label}
            hasPadding
            hasTitleBorder
            initialState={initiallyOpen}
            ref={ref}
          >
            <div className={"section-collapse-content"}>
              {section?.description && (
                <div className={"section-content-description"}>
                  {section?.description}
                </div>
              )}
              <div className={"section-content-rows"}>{content}</div>
              {showConfirmButton && (
                <TextButton
                  data-testid={`confirm-${section?.name}`}
                  buttonStyle={"primary"}
                  wrapperClassName={"section-button"}
                  disabled={confirmButtonDisabled}
                  label={`Confirm ${section?.label}`}
                  type={"button"}
                  onClick={onConfirmButtonClicked}
                />
              )}
            </div>
          </Collapse>
        ) : (
          content
        )}
      </div>
    );
  }
);

export default Section;
